import isEmpty from "lodash-es/isEmpty";
import { Alert, Button, DeleteIcon, Space } from "melco-ui";
import { useTranslation } from "react-i18next";
import { useConfirm } from "../../../hooks/useConfirm";
import { Key } from "antd/lib/table/interface";
import { SharedserviceMelcoModelsAlphabetCollection } from "melco-shared-logic";
import { useShowWarningForRowKeys } from "../../../hooks/table/useShowWarningForRowKeys";
import { useDeleteFontCollections } from "../../../hooks/libraries/fontCollections/useDeleteFontCollections";

type BulkActionDeleteFontCollectionsProps<T> = {
  selectedRowKeys: Key[];
  setSelectedRowKeys: (keys: Key[]) => void;
  refetch?: () => Promise<any>;
  data?: T[];
};

export const BulkActionDeleteFontCollections: React.FC<
  BulkActionDeleteFontCollectionsProps<SharedserviceMelcoModelsAlphabetCollection>
> = ({ selectedRowKeys, setSelectedRowKeys, refetch, data }) => {
  const { t } = useTranslation();
  const deleteFontCollections = useDeleteFontCollections();
  const showWarningForRowKeys = useShowWarningForRowKeys(selectedRowKeys, data);

  const confirmDelete = useConfirm(
    async () => {
      const successfullyDeletedIds = await deleteFontCollections(
        selectedRowKeys.map((key) => key.toString())
      );

      if (refetch) {
        refetch();
      }

      // keep items that were not deleted selected
      setSelectedRowKeys(
        selectedRowKeys.filter(
          (id) => !successfullyDeletedIds.includes(id.toString())
        )
      );
    },
    {
      translationPrefix: "font_collections.actions.delete",
      translationOptions: {
        count: selectedRowKeys.length,
      },
      content: showWarningForRowKeys.length && (
        <Space direction="vertical" size="small">
          {t("font_collections.actions.delete.confirmation.message", {
            count: selectedRowKeys.length,
          })}
          <Alert
            type="warning"
            withBackground
            description={t(
              "font_collections.actions.delete.confirmation.warning",
              {
                count: showWarningForRowKeys.length,
              }
            )}
            showIcon={false}
          />
        </Space>
      ),
    }
  );

  if (isEmpty(selectedRowKeys)) {
    return null;
  }

  return (
    <Button
      key="bulk-delete"
      icon={<DeleteIcon />}
      onClick={() => {
        confirmDelete();
      }}
    >
      {t("font_collections.actions.delete.button")}
    </Button>
  );
};
